import { useState, useEffect, useCallback } from 'react';
import CheckIcon from '@rsuite/icons/Check';
import { Sidenav, Nav } from 'rsuite';
import PieChartIcon from '@rsuite/icons/PieChart';
import GroupIcon from '@rsuite/icons/legacy/Group';
import ShopIcon from '@rsuite/icons/legacy/ShoppingBag';
import MagicIcon from '@rsuite/icons/legacy/Magic';
import GearIcon from '@rsuite/icons/legacy/Gear';
import NoticeIcon from '@rsuite/icons/Notice';
import api from '../../services/api';
import '../style.css';
import { Container } from 'rsuite';
import { HeaderTitle } from './styles';
import { useAuth } from '../../hooks/auth';
import { useCompany } from '../../hooks/useCompany';
import { ContentHeader } from '../../components/ContentHeader';
import DuvidasButton from '../../components/DuvidasButton';
import { useCustomTheme } from '../../hooks/useCustomTheme';
import { CustomButton } from '../../components/CustomButton';
import { toastSuccess, toastError } from '../../toasts';
import { defaultConfigState } from './constants';
import { parseConfigStateToConfig, parseConfigToConfigState } from './utils';
import { truthy } from '../../utils/utils';
import { useConfig } from '../../hooks/config';
import Notifications from './Notifications';
import LogoThemes from './LogoThemes';
import Advanced from './Advanced';
import DigitalMenu from './DigitalMenu';
import PizzaSettings from './PizzaSettings';
import Store from './Store';
import { FaCog } from 'react-icons/fa';

const GTM_ID_REGEX = /^GTM-[A-Z0-9]{4,12}$/;

export default function Config() {
  const { id_empresa } = useAuth();
  const { updateConfig } = useConfig();
  const { company } = useCompany();
  const [config, setConfig] = useState(defaultConfigState);
  const [active, setActive] = useState('loja');
  const { theme } = useCustomTheme();

  const onChange = (label) => {
    return (value) => {
      setConfig((state) => ({ ...state, [label]: value }));
    };
  };

  const change = (label, value) => {
    setConfig((state) => ({ ...state, [label]: value }));
  };

  const atualizar = useCallback(async () => {
    await api
      .get(`configuracao/encontrar/${id_empresa}`)
      .then((res) => {
        const data = res.data;

        for (const key in data) {
          if (data[key] === 'null') {
            data[key] = null;
          }
        }

        setConfig(parseConfigToConfigState(data));
      })
      .catch((error) => console.log({ error }));
  }, [id_empresa]);

  const salvar = async () => {
    const request_data = parseConfigStateToConfig(config);

    if (
      request_data.GoogleTagManager &&
      !GTM_ID_REGEX.test(request_data.GoogleTagManager)
    ) {
      toastError('ID do Google Tag Manager inválido. Use o formato GTM-XXXXXXX');
      return;
    }

    if (truthy(config.existe)) {
      updateSettings(request_data);
    } else {
      createSettings(request_data);
    }
  };

  const createSettings = async (request_data) => {
    try {
      const createData = { ...request_data, IdEmpresa: id_empresa };
      await api.post('configuracao/criar', createData);
      toastSuccess('Dados cadastrados');
      atualizar();
    } catch (error) {
      console.log('Erro: ' + error);
      toastError('Não foi possivel alterar configurações');
    }
  };

  const updateSettings = async (request_data) => {
    try {
      const updateData = { ...request_data, Id: config.idConfig };
      const updatedData = await api.patch(
        `configuracao/alterar/${config.idConfig}`,
        updateData,
      );
      updateConfig(updatedData.data);
      toastSuccess('Dados alterados', 'Informação');
      atualizar();
    } catch (error) {
      console.log('Erro: ' + error);
      toastError('Não foi possivel alterar configurações');
    }
  };

  useEffect(() => {
    atualizar();
  }, [atualizar]);

  return (
    <Container
      style={{
        padding: '10px 10px 0',
        background: theme.colors.background,
        height: 'calc(100vh - 60px)',
      }}
    >
      <ContentHeader
        style={{ zIndex: 99 }}
        title="Configurações"
        subtitle="Gerencie as configurações da sua empresa"
        icon={FaCog}
        thirdComponent={
          <>
            <DuvidasButton tela="configuracoes" />
            <CustomButton appearance="primary" onClick={() => salvar()}>
              <CheckIcon style={{ marginRight: 6 }} /> Salvar Alterações
            </CustomButton>
          </>
        }
      />

      <div
        style={{
          flexDirection: 'row',
          display: 'flex',
          overflow: 'hidden',
        }}
      >
        <div style={{ width: 240 }}>
          <Sidenav
            defaultOpenKeys={['3', '4']}
            style={{ background: theme.colors.background }}
          >
            <Sidenav.Header>
              <HeaderTitle>Módulos</HeaderTitle>
            </Sidenav.Header>
            <Sidenav.Body>
              <Nav activeKey={active}>
                <Nav.Item
                  eventKey="loja"
                  onClick={() => setActive('loja')}
                  icon={<ShopIcon />}
                  style={{
                    background: theme.colors.background,
                    color:
                      active === 'loja'
                        ? theme.colors.primary
                        : theme.colors.text,
                  }}
                >
                  Loja
                </Nav.Item>
                {!!company.CardapioAtivo && (
                  <Nav.Item
                    eventKey="cardapioDigital"
                    onClick={() => setActive('cardapioDigital')}
                    icon={<GroupIcon />}
                    style={{
                      background: theme.colors.background,
                      color:
                        active === 'cardapioDigital'
                          ? theme.colors.primary
                          : theme.colors.text,
                    }}
                  >
                    Cardápio Digital
                  </Nav.Item>
                )}
                <Nav.Item
                  eventKey="pizzas"
                  onClick={() => setActive('pizzas')}
                  icon={<PieChartIcon />}
                  style={{
                    background: theme.colors.background,
                    color:
                      active === 'pizzas'
                        ? theme.colors.primary
                        : theme.colors.text,
                  }}
                >
                  Conf. Pizza
                </Nav.Item>
                <Nav.Item divider />
                <Nav.Item
                  eventKey="temas"
                  onClick={() => setActive('temas')}
                  icon={<MagicIcon />}
                  style={{
                    background: theme.colors.background,
                    color:
                      active === 'temas'
                        ? theme.colors.primary
                        : theme.colors.text,
                  }}
                >
                  Temas e Logos
                </Nav.Item>
                <Nav.Item
                  eventKey="notificacoes"
                  onClick={() => setActive('notificacoes')}
                  icon={<NoticeIcon />}
                  style={{
                    background: theme.colors.background,
                    color:
                      active === 'notificacoes'
                        ? theme.colors.primary
                        : theme.colors.text,
                  }}
                >
                  Notificações
                </Nav.Item>
                <Nav.Item
                  eventKey="advanced"
                  onClick={() => setActive('advanced')}
                  icon={<GearIcon />}
                  style={{
                    background: theme.colors.background,
                    color:
                      active === 'advanced'
                        ? theme.colors.primary
                        : theme.colors.text,
                  }}
                >
                  Avançado
                </Nav.Item>
              </Nav>
            </Sidenav.Body>
          </Sidenav>
        </div>
        <view
          style={{
            justifyContent: 'center',
            display: 'flex',
            width: '100%',
            overflowY: 'auto',
          }}
        >
          {active === 'pizzas' && (
            <PizzaSettings config={config} change={change} />
          )}
          {active === 'loja' && (
            <Store
              config={config}
              change={change}
              onChange={onChange}
              setConfig={setConfig}
              atualizar={atualizar}
            />
          )}
          {active === 'temas' && (
            <LogoThemes
              config={config}
              change={change}
              atualizar={atualizar}
              onChange={onChange}
            />
          )}
          {active === 'notificacoes' && (
            <Notifications config={config} onChange={onChange} />
          )}
          {!!company.CardapioAtivo && active === 'cardapioDigital' && (
            <DigitalMenu config={config} change={change} />
          )}
          {active === 'advanced' && (
            <Advanced config={config} change={change} />
          )}
        </view>
      </div>
    </Container>
  );
}
